import React, { useRef } from "react";
import { Typography, Button, Loading } from "../../components/common";
import { Upload, X, Image, AlertCircle } from "lucide-react";
import { useBrandingHook } from "./useBrandingHook";

const Branding: React.FC = () => {
  const {
    organisation,
    loading,
    error,
    isUploading,
    currentLogo,
    selectedFile,
    isReadOnly,
    handleFileSelected,
    handleSave,
    handleCancel,
  } = useBrandingHook();

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-gray-600 font-medium mt-4 flex">
            {" "}
            <Loading className="pr-2" /> Loading...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Typography variant="h4" className="text-red-600 mb-2">
            Error
          </Typography>
          <Typography variant="p" className="text-gray-600">
            {error}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <Typography variant="h2" className="text-primary font-semibold mb-2">
          Brand Logo Management
        </Typography>
        <Typography variant="p" className="text-gray-600">
          Upload and manage your organization's logo. Supported formats: PNG,
          JPG, SVG (max 5MB)
        </Typography>
      </div>

      {/* Read-only banner for internal users */}
      {isReadOnly && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <span className="text-amber-600 text-sm font-medium">
            You have read-only access — logo uploads are disabled
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Current Logo Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Image size={20} className="text-primary" />
            <Typography variant="h5" className="font-semibold">
              Current Logo
            </Typography>
          </div>
          <Typography variant="p" className="text-sm text-gray-600 mb-4">
            This is your currently active brand logo
          </Typography>

          {organisation?.logo_url ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 h-48 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center">
                <img
                  src={organisation.logo_url}
                  alt="Current organization logo"
                  className="w-32 h-32 object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 h-48 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center text-gray-400">
                <Image size={48} />
                <Typography variant="p" className="text-sm mt-2">
                  No logo uploaded
                </Typography>
              </div>
            </div>
          )}
        </div>

        {/* Upload New Logo Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload size={20} className="text-primary" />
            <Typography variant="h5" className="font-semibold">
              Upload New Logo
            </Typography>
          </div>
          <Typography variant="p" className="text-sm text-gray-600 mb-4">
            Replace your current logo with a new one
          </Typography>

          <div className="space-y-4">
            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50 transition-colors h-48 flex items-center justify-center ${isReadOnly ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 cursor-pointer"}`}
              onClick={() => !isReadOnly && fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                if (!isReadOnly) {
                  e.currentTarget.classList.add("border-[rgb(var(--pd-focus))]", "bg-[rgb(var(--pd-surface))]");
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove(
                  "border-[rgb(var(--pd-focus))]",
                  "bg-[rgb(var(--pd-surface))]"
                );
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove(
                  "border-[rgb(var(--pd-focus))]",
                  "bg-[rgb(var(--pd-surface))]"
                );
                if (isReadOnly) return;
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleFileSelected(files[0]);
                }
              }}
            >
              <div className="flex flex-col items-center justify-center relative w-full">
                {selectedFile && currentLogo ? (
                  <>
                    <img
                      src={currentLogo}
                      alt="Logo preview"
                      className="w-32 h-32 object-contain mb-4"
                    />
                    <Typography
                      variant="p"
                      className="text-sm font-medium text-gray-700"
                    >
                      {selectedFile.name}
                    </Typography>
                    <button
                      className="absolute right-0 top-0 text-red-500 hover:bg-red-50 rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileSelected(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                    >
                      <X size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <Upload size={48} className="text-gray-400 mb-4" />
                    <Typography
                      variant="p"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      Drop your logo here, or browse files
                    </Typography>
                    <Typography variant="small" className="text-gray-500">
                      PNG, JPG, SVG up to 5MB
                    </Typography>
                  </>
                )}
              </div>
            </div>

            {/* Hidden File Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleFileSelected(e.target.files[0]);
                }
              }}
              accept="image/*"
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Logo Guidelines Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle size={20} className="text-primary" />
          <Typography variant="h5" className="font-semibold">
            Logo Guidelines
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Typography variant="h6" className="font-medium mb-3">
              Recommended Specifications
            </Typography>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                Minimum size: 200x120 pixels
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                Maximum size: 800x600 pixels
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                Aspect ratio: 16:9 or 4:3 preferred
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                File size: Under 5MB
              </li>
            </ul>
          </div>

          <div>
            <Typography variant="h6" className="font-medium mb-3">
              Best Practices
            </Typography>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                Use transparent background (PNG)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                Ensure logo works on light backgrounds
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                High resolution for crisp display
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                Simple, scalable design
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 mt-6">
        <Button
          variant="outline"
          size="medium"
          onClick={handleCancel}
          disabled={isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="filled"
          size="medium"
          onClick={handleSave}
          disabled={isUploading || !selectedFile || isReadOnly}
          startIcon={isUploading && <Loading />}
        >
          {isUploading ? "Saving..." : "Save Logo"}
        </Button>
      </div>
    </div>
  );
};

export default Branding;
